<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { version } from '../../../package.json';
import { useSessionStore } from '@/store/sessionStore';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const sessionStore = useSessionStore();

const switchLanguage = (langCode: string) => {
  locale.value = langCode;
  localStorage.setItem('locale', langCode);
};

onMounted(() => {
  const savedLocale = localStorage.getItem('locale');
  if (savedLocale) {
    locale.value = savedLocale;
  }
});
const backendVersion = ref(version);
const gitChangelogUrl = ref('https://github.com/tuev-ai-lab/AIRiN/blob/AIRiN-2.0-dev/README.md');

const isAilabUser = computed(() => {
  const role = (sessionStore.user as { role?: string } | null)?.role ?? '';
  return role.startsWith('ai_lab');
});

const tenant = computed(() => sessionStore.getTenant() || {
    name: '',
    domains: [],
    settings: {
        defaultLanguage: ''
    },
    adminSettings: {
        enabledLanguages: [],
        reportConnectionString: ''
    },
    theme: null,
    mongoDBisConnected: false
});

const op = ref();


const openDebugFrontend = (domain: string) => {
  let targetOrigin: string;
  if (domain === 'localhost') {
    targetOrigin = 'http://localhost:5173';
  } else {
    targetOrigin = 'https://' + domain;
  }
  const frontendWindow = window.open(targetOrigin, '_blank');

  const handleReadyMessage = (event: MessageEvent) => {
    if (event.origin === targetOrigin && event.data === 'ready') {
      frontendWindow?.postMessage({ token: localStorage.getItem('token') }, targetOrigin);
      window.removeEventListener('message', handleReadyMessage);
    }
  };

  window.addEventListener('message', handleReadyMessage);

  setTimeout(() => {
    window.removeEventListener('message', handleReadyMessage);
  }, 5000);
};


const toggle = (event: Event) => {
  op.value.toggle(event);
}

</script>

<template>
  <div>
    <footer class="footerBar" id="footerBar">
      <div class="content d-flex justify-content-between text-center w-100 align-items-center">
        <span class="d-flex">v{{ backendVersion }}</span>
        <span class="d-flex">&copy; {{ new Date().getFullYear() }} TÜV AI.Lab</span>

        <span v-if="isAilabUser" class="language-switcher d-flex">
          <a @click="switchLanguage('en')" :class="{ active: locale === 'en' }">en</a>
          <span class="separator">|</span>
          <a @click="switchLanguage('de')" :class="{ active: locale === 'de' }">de</a>
        </span>

        <a target="_blank" rel="noopener" @click="toggle" class="changelog-link d-flex">
            Open Debugging Interface
        </a>
      </div>
    </footer>


    <Popover ref="op">
      <div class="flex flex-col gap-4 w-[25rem]">
        <div>
          <span class="font-medium block mb-2">Select a Domain</span>
          <InputGroup v-for="domain in tenant.domains" :key="domain" class="mb-2">
            <InputText :value="domain" disabled readonly class="w-[25rem]">
            </InputText>
            <InputGroupAddon>
              <FontAwesomeIcon icon="fa-solid fa-arrow-right" class="text-gray-500 cursor-pointer" @click="openDebugFrontend(domain)" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
    </Popover>
  </div>
</template>

<style scoped>
.footerBar {
  margin-top: 1rem;
  height: var(--header-height);
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
  z-index: 99;
  transition: 0.5s;
  color: white;
  font-size: 0.875rem;
}

.footerBar .content {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 90%;
}

.footerBar .changelog-link {
  color: #00bfff;
  text-decoration: none;
  font-weight: bold;
  transition: color 0.3s ease;
}

.footerBar .changelog-link:hover {
  color: #1e90ff;
}

.language-switcher a {
  color: #888;
  cursor: pointer;
  transition: color 0.2s;
}

.language-switcher a:hover,
.language-switcher a.active {
  color: #00bfff;
}

.language-switcher .separator {
  margin: 0 0.25rem;
  color: #555;
}

@media screen and (min-width: 768px) {
  .footerBar {
    height: calc(var(--header-height) - 1rem);
    padding: 0 2rem;
  }
}
</style>
